import type { ServiceDetail } from "./types";

export const INTERNAZIONALIZZAZIONE: ServiceDetail = {
  slug: "internazionalizzazione",
  category: "business",
  name: "Internazionalizzazione",
  hero: {
    eyebrow: "Internazionalizzazione",
    title: "Espansione misurata sui mercati esteri, non fiere a casaccio.",
    subtitle:
      "Portiamo le PMI italiane su UE, UK, USA, EAU e LATAM con un metodo strutturato: assessment readiness, market scoring quantitativo, scouting partner locali e go-to-market localizzato. Sede a Roma, certificati ISO 9001:2015, rete consolidata di partner all'estero.",
    bullets: [
      "Export readiness check in 2 settimane",
      "Market selection report con scoring quantitativo",
      "Scouting di distributori e clienti chiave sul territorio",
      "Setup entità locale o partnership con tutela contrattuale",
    ],
  },
  problem: [
    "Per molte PMI italiane il mercato domestico inizia a saturarsi: marginalità che si comprime, clienti che chiedono sconti, concorrenza che si affolla. L'estero diventa l'unica leva di crescita reale, ma la maggior parte delle aziende lo affronta con fiere casuali, contatti improvvisati e nessuna strategia di selezione del mercato.",
    "I founder che provano da soli si scontrano con tre muri: mancanza di network locale (servono distributori e referenti che si fidino di te), complessità fiscale e legale internazionale (IVA intracomunitaria, dazi doganali, contratti distributivi, IP protection, costituzione branch o subsidiary) e la barriera di lingua e cultura, che va oltre la traduzione del sito.",
    "A questo si aggiunge la difficoltà di scegliere il primo mercato target: \"andiamo in Germania perché siamo vicini\" o \"in USA perché c'è il mercato grande\" non sono strategie. Senza market scoring quantitativo, si bruciano 12-18 mesi e budget significativi su mercati sbagliati prima di capirlo.",
  ],
  approach: [
    "Assessment export readiness (2 settimane): analisi della tua azienda — prodotto, marginalità, capacità produttiva, lingue parlate dal team, certificazioni, posizionamento — per capire se sei pronta a esportare e su quali mercati hai vantaggio competitivo reale. Output: readiness check con gap da chiudere prima del go-live.",
    "Selezione mercato + posizionamento (4-8 settimane): analisi quantitativa di 6-10 mercati candidati su domanda, concorrenza, barriere all'entrata, marginalità attesa, accordi commerciali UE. Da qui scegliamo insieme i 2-3 mercati prioritari e definiamo posizionamento, pricing locale e value proposition adattata.",
    "Go-to-market locale (3-6 mesi): scouting partner sul territorio (distributori, agenti, system integrator), prima rete di contatti chiave, sito multilingua, campagne digital geo-targettizzate, eventuale costituzione di entità locale o partnership con camere di commercio italiane all'estero.",
    "Scaling (ongoing): KPI dashboard export con monitoraggio per mercato, affiancamento commerciale al team che gestisce i clienti esteri, iterazione mensile sui mercati che rispondono e disinvestimento controllato da quelli che non scalano.",
  ],
  deliverables: [
    {
      title: "Export readiness check",
      detail:
        "Assessment di prodotto, marginalità, capacità produttiva e gap operativi prima di puntare sui mercati esteri. Output: report con priorità e quick win.",
    },
    {
      title: "Market selection report con scoring",
      detail:
        "Analisi quantitativa di 6-10 mercati candidati con scoring multicriterio (domanda, competizione, barriere, marginalità attesa) e shortlist 2-3 mercati prioritari.",
    },
    {
      title: "Scouting partner e clienti chiave",
      detail:
        "Identificazione, contatto e qualifica di distributori, agenti, system integrator e potenziali clienti chiave nei mercati selezionati. Lista contatti qualificati e introduzioni.",
    },
    {
      title: "Setup entità locale o partnership",
      detail:
        "Costituzione branch / subsidiary o accordi distributivi con tutela contrattuale (contratto in lingua, IP protection, clausole di esclusiva, governance fiscale).",
    },
    {
      title: "Piano marketing localizzato",
      detail:
        "Sito multilingua e SEO locale, campagne advertising geo-targettizzate, materiali commerciali tradotti e adattati culturalmente, brand book per il mercato target.",
    },
    {
      title: "KPI export dashboard",
      detail:
        "Dashboard con metriche per mercato (lead, opportunity, ordini, fatturato, margine, costo acquisizione cliente) e revisione mensile per iterare la strategia.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1-2 — Assessment",
      text: "AB MARK conduce export readiness check (prodotto, marginalità, capacità produttiva, certificazioni, lingue). Tu fornisci accesso ai dati commerciali e produttivi. Output: report condiviso con gap da chiudere.",
    },
    {
      step: "02",
      title: "Mese 1-2 — Selezione mercato",
      text: "AB MARK conduce market scoring quantitativo su 6-10 candidati e propone shortlist 2-3 mercati. Tu validi la scelta finale e approvi pricing/posizionamento locale. Output: market selection report.",
    },
    {
      step: "03",
      title: "Mese 3-6 — Go-to-market",
      text: "AB MARK gestisce scouting partner, costituzione entità o accordi distributivi, sito multilingua, campagne digital. Tu partecipi alle introduzioni con i partner key e firmi i contratti. Output: prima rete attiva.",
    },
    {
      step: "04",
      title: "Ongoing — Scaling",
      text: "AB MARK mantiene la KPI dashboard export e affianca il team commerciale interno. Tu condividi feedback dai clienti esteri e prioritizzi gli investimenti. Revisione mensile per iterare per mercato.",
    },
  ],
  faq: [
    {
      q: "Quali mercati esteri seguite direttamente?",
      a: "Per esperienza diretta del nostro team e della nostra rete, seguiamo: Unione Europea (in particolare DACH, Francia, Spagna, Benelux, Nord Europa), UK, USA e Canada, Emirati Arabi Uniti e Arabia Saudita, e LATAM (con focus su Messico, Brasile, Argentina). Per mercati asiatici (Cina, Giappone, Sud-Est Asiatico) ci appoggiamo a partner specializzati con ufficio locale.",
    },
    {
      q: "Fornite supporto linguistico al nostro team?",
      a: "Sì. Lavoriamo direttamente in italiano, inglese, francese, spagnolo, tedesco e portoghese. Per lingue specifiche (arabo, mandarino, russo, giapponese) abbiamo traduttori e interpreti madrelingua nel network. Forniamo anche brand book e linee guida culturali per evitare errori di posizionamento sui mercati target.",
    },
    {
      q: "Quanto costa mediamente costituire una entità locale all'estero?",
      a: "Dipende dal paese: una branch in UE costa tipicamente 5.000-15.000 € di setup + 3.000-8.000 €/anno di compliance; una LLC negli USA 3.000-8.000 € di setup + 4.000-10.000 €/anno; una entità negli EAU (free zone) 8.000-20.000 € di setup. In molti casi conviene partire con un accordo distributivo (zero costi di setup) e costituire l'entità solo dopo i primi 12 mesi di traction.",
    },
    {
      q: "Lavorate con le Camere di Commercio italiane all'estero?",
      a: "Sì, attivamente. Le Camere di Commercio Italiane all'Estero (CCIE) e ITA - Italian Trade Agency sono partner istituzionali che usiamo per facilitare introduzioni B2B, accedere a fondi SIMEST per l'internazionalizzazione e organizzare missioni commerciali. Ti aiutiamo anche a presentare domande SIMEST quando il vostro progetto è ammissibile.",
    },
    {
      q: "Vi occupate degli aspetti fiscali e contrattuali internazionali?",
      a: "Sì, in collaborazione con studi legali e tributari partner specializzati: contratti distributivi e di agenzia in lingua, IVA intracomunitaria e VIES, dazi doganali e codici HS, IP protection internazionale (marchi UE/WIPO), GDPR per i mercati extra-UE, costituzione di branch o subsidiary, transfer pricing per gruppi multinazionali.",
    },
    {
      q: "Quanto tempo serve per chiudere il primo cliente estero?",
      a: "Dipende molto dal settore e dal canale. Su B2B con vendita diretta vediamo tipicamente i primi contatti qualificati dal mese 4-5 e il primo ordine tra il mese 6 e il mese 9 dal kick-off. Su B2B con distributori il primo contratto distributivo si chiude in 3-4 mesi e i primi ordini arrivano nel mese 5-7. Su B2C ecommerce la pipeline è più rapida ma i volumi iniziali più piccoli.",
    },
  ],
  seo: {
    title: "Internazionalizzazione PMI Italiane | Export | AB MARK",
    description:
      "Consulenza export PMI: internazionalizzazione PMI italiane ed espansione estero aziende su UE, UK, USA, EAU e LATAM, scouting partner e setup entità locale.",
  },
};
